/**
 * Every /audience read (spec §2.2), each under its own run key through D1's useKeyedRead:
 * - facts: the scope, the period's two dates and the attempt (R10), never the token: 'mtd' and an equal custom range
 *   read once;
 * - freshness: the workspace alone (D1's loadChannelFreshness); none in all workspaces, so the pill falls back to the
 *   facts (R1).
 * One attempt counter sits in both keys: retry() re-reads both once. A null scope reads nothing.
 *
 * No filter is an input: channel, objective, currency, Active and "แสดงทั้งหมด" are applied by buildAudienceView on
 * the client and read nothing.
 *
 * @module hooks/useAudienceData
 */
import { useCallback, useMemo, useState } from 'react';
import { readAudienceFacts } from '@/services/audience/audienceReaders';
import type { AudienceFact } from '@/services/audience/audienceTypes';
import { loadChannelFreshness } from '@/services/looker/lookerAppLoaders';
import type { FreshChannel } from '@/services/looker/lookerAppData';
import { bangkokToday } from '@/services/looker/lookerDateRange';
import type { OverviewWindows, ReadState } from '@/services/overview/overviewTypes';
import { overviewWindowsOf } from '@/services/overview/overviewWindows';
import type { ChannelFreshnessState } from './useLookerChannelFreshness';
import type { OverviewScope } from './useOverviewData';
import { useKeyedRead } from './useKeyedRead';

export interface AudienceData {
  /** null = no bounded range (an empty "this month", a refused custom range): the facts are not read; the freshness still is. */
  windows: OverviewWindows | null;
  facts: ReadState<AudienceFact[]>;
  /** idle in all workspaces. */
  freshness: ChannelFreshnessState;
  /** Re-reads both once. */
  retry: () => void;
}

const unscoped = (): Promise<never> => Promise.reject(new Error('audience: no scope'));

/** The pill's state from the read (useCreativeData's private rule, written again: that hook exports nothing else). */
const freshnessOf = (read: ReadState<FreshChannel[]>): ChannelFreshnessState => {
  if (read.status === 'ready') return { status: 'ready', channels: read.data };
  return read.status === 'failed' ? { status: 'failed' } : read.status === 'loading' ? { status: 'loading' } : { status: 'idle' };
};

export const useAudienceData = (scope: OverviewScope | null, token: string): AudienceData => {
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const windows = useMemo(() => overviewWindowsOf(token, bangkokToday()), [token]);
  const range = windows?.current ?? null;
  const ids = scope?.workspaceIds ?? [];
  const ws = scope?.workspaceId ?? null;
  const at = `a:${attempt}`;
  const factsKey = scope && range ? `facts|${ws ?? 'all'}|${ids.join(',')}|${range.start}..${range.end}|${at}` : '';
  const freshnessKey = scope && ws ? `fr|${ws}|${at}` : '';

  const facts = useKeyedRead<AudienceFact[]>(factsKey, () => (range ? readAudienceFacts(ids, range) : unscoped()), 'AudienceFacts');
  const freshnessRead = useKeyedRead<FreshChannel[]>(freshnessKey, () => (ws ? loadChannelFreshness(ws) : unscoped()), 'AudienceFreshness');
  const freshness = useMemo(() => freshnessOf(freshnessRead), [freshnessRead]);

  return useMemo(() => ({ windows, facts, freshness, retry }), [windows, facts, freshness, retry]);
};
